import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2, LayoutGrid } from "lucide-react";
import { QUESTIONS, LIKERT_OPTIONS } from "../data/questions";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/axiosClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

const Quiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // /couple/:id/take -> partner mode
  const coupleId = location.pathname.startsWith("/couple/") ? params.id : null;
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // {1: 4, 2: 3, ...}
  const [submitting, setSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  // Track a pending auto-advance timer so we can cancel it when the user
  // manually navigates or picks another option. Without this, fast clicks
  // can queue a stale timer that then skips a question.
  const advanceTimerRef = useRef(null);
  const cancelAutoAdvance = () => {
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  };
  useEffect(() => () => cancelAutoAdvance(), []);

  const total = QUESTIONS.length;
  const current = QUESTIONS[index];
  const answeredCount = Object.keys(answers).length;
  const progress = useMemo(() => (answeredCount / total) * 100, [answeredCount, total]);

  const selected = answers[current.number];
  const allAnswered = answeredCount === total;

  const missingNumbers = useMemo(
    () => QUESTIONS.filter((q) => answers[q.number] == null).map((q) => q.number),
    [answers]
  );

  const handleSelect = (value) => {
    const wasUnanswered = answers[current.number] == null;
    cancelAutoAdvance();
    setAnswers((prev) => ({ ...prev, [current.number]: value }));
    // Only auto-advance on FIRST answer for this question and only if there's
    // a next unanswered slide. If the user is reviewing/changing an existing
    // answer, stay put so they can confirm.
    if (wasUnanswered && index < total - 1) {
      advanceTimerRef.current = setTimeout(() => {
        setIndex((i) => Math.min(i + 1, total - 1));
        advanceTimerRef.current = null;
      }, 280);
    }
  };

  const goPrev = () => { cancelAutoAdvance(); setIndex((i) => Math.max(i - 1, 0)); };
  const goNext = () => { cancelAutoAdvance(); setIndex((i) => Math.min(i + 1, total - 1)); };
  const jumpTo = (questionNumber) => {
    cancelAutoAdvance();
    const idx = QUESTIONS.findIndex((q) => q.number === questionNumber);
    if (idx >= 0) setIndex(idx);
  };

  const submit = async () => {
    if (!allAnswered) {
      const first = missingNumbers[0];
      toast.error(
        `Please answer all 24 questions. ${missingNumbers.length} remaining — jumping to question ${first}.`
      );
      jumpTo(first);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { answers: QUESTIONS.map((q) => answers[q.number]) };
      if (coupleId) {
        await api.post(`/couples/${coupleId}/partner`, payload);
        navigate(`/couple/${coupleId}`);
      } else {
        const { data } = await api.post("/submissions", payload);
        navigate(`/results/${data.id}`);
      }
    } catch (e) {
      console.error(e);
      toast.error(formatApiError(e, "Something went wrong submitting your quiz. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="quiz-page">
      {/* Header / progress */}
      <header className="border-b border-[#E8E2D9] bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-3 gap-3">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A8782] font-semibold">
              Question <span data-testid="question-counter">{index + 1}</span> of {total}
            </span>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#8A8782]" data-testid="progress-percent">
                {Math.round(progress)}% complete
              </span>
              <button
                onClick={() => setReviewOpen(true)}
                data-testid="open-review-button"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D2A26] bg-white border-2 border-[#E8E2D9] hover:border-[#C86B5E] rounded-full px-3 py-1.5 transition-all active:scale-95"
              >
                <LayoutGrid className="w-3.5 h-3.5" strokeWidth={2} />
                Review
              </button>
            </div>
          </div>
          <div
            className="w-full h-2 bg-[#E8E2D9] rounded-full overflow-hidden"
            data-testid="quiz-progress"
          >
            <motion.div
              className="h-full bg-[#C86B5E] rounded-full"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </div>
      </header>

      {/* Question */}
      <main className="flex-1 flex items-start">
        <div className="max-w-2xl mx-auto px-6 py-12 md:py-16 w-full">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.number}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            >
              <p className="text-xs uppercase tracking-[0.18em] text-[#C86B5E] font-semibold mb-4">
                Statement {current.number}
              </p>
              <h2
                className="font-serif-display text-2xl sm:text-3xl lg:text-4xl text-[#2D2A26] leading-tight font-medium"
                data-testid="question-text"
              >
                {current.text}
              </h2>

              <div className="flex flex-col gap-3 w-full mt-10" data-testid="likert-scale">
                {LIKERT_OPTIONS.map((opt, i) => {
                  const active = selected === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => handleSelect(opt.value)}
                      data-testid={`likert-option-${opt.value}`}
                      className={[
                        "w-full text-left px-6 py-5 rounded-2xl border-2 transition-all flex items-center justify-between group",
                        active
                          ? "border-[#C86B5E] bg-[#F8EBE9] text-[#2D2A26] shadow-sm"
                          : "border-[#E8E2D9] bg-white hover:border-[#C86B5E]/60 hover:bg-[#FDF5EE] text-[#2D2A26]",
                      ].join(" ")}
                    >
                      <span className="flex items-center gap-4">
                        <span
                          className={[
                            "w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border transition-colors",
                            active
                              ? "bg-[#C86B5E] text-white border-[#C86B5E]"
                              : "bg-white text-[#8A8782] border-[#E8E2D9] group-hover:border-[#C86B5E]",
                          ].join(" ")}
                        >
                          {active ? <Check className="w-4 h-4" strokeWidth={2.5} /> : opt.value}
                        </span>
                        <span className="text-base sm:text-lg font-medium">{opt.label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Nav */}
          <div className="flex items-center justify-between mt-12">
            <button
              onClick={goPrev}
              disabled={index === 0}
              data-testid="prev-question-button"
              className="inline-flex items-center gap-2 text-[#5C5A56] hover:text-[#2D2A26] disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={2} />
              Previous
            </button>

            {index < total - 1 ? (
              <button
                onClick={goNext}
                disabled={selected == null}
                data-testid="next-question-button"
                className="inline-flex items-center gap-2 bg-[#2D2A26] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1f1d1a] disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-[0.98]"
              >
                Next
                <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-quiz-button"
                className="inline-flex items-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3 font-medium text-sm hover:bg-[#B35B4E] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Scoring…
                  </>
                ) : (
                  <>
                    See my results
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </>
                )}
              </button>
            )}
          </div>

          {!allAnswered && index === total - 1 && (
            <div
              className="mt-6 rounded-2xl border border-[#E8E2D9] bg-[#FDF5EE] p-5"
              data-testid="missing-answers-helper"
            >
              <p className="text-sm font-medium text-[#2D2A26]">
                {missingNumbers.length} question{missingNumbers.length === 1 ? "" : "s"} still need an answer.
              </p>
              <p className="text-xs text-[#8A8782] mt-1 mb-3">Tap a number to jump back and answer it.</p>
              <div className="flex flex-wrap gap-2">
                {missingNumbers.map((n) => (
                  <button
                    key={n}
                    onClick={() => jumpTo(n)}
                    data-testid={`jump-to-question-${n}`}
                    className="inline-flex items-center justify-center min-w-9 h-9 px-3 rounded-full bg-white border-2 border-[#E8E2D9] hover:border-[#C86B5E] text-sm font-semibold text-[#2D2A26] transition-all active:scale-95"
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Review answers dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent
          className="max-w-md bg-[#FDFBF7] border-[#E8E2D9]"
          data-testid="review-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl text-[#2D2A26] font-semibold">
              Review your answers
            </DialogTitle>
            <DialogDescription className="text-[#5C5A56]">
              <span className="text-[#2D2A26] font-semibold">{answeredCount}</span> of {total} answered
              {missingNumbers.length > 0 && (
                <>
                  {" · "}
                  <span className="text-[#C86B5E] font-semibold">{missingNumbers.length} remaining</span>
                </>
              )}
              . Tap any question to jump to it.
            </DialogDescription>
          </DialogHeader>

          {/* Legend */}
          <div className="flex items-center gap-4 text-[11px] text-[#5C5A56]">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#8A9A86]" /> Answered
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#C86B5E]" /> Current
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-[#E8E2D9] border border-[#D1C7B8]" /> Missing
            </span>
          </div>

          {/* Tile grid */}
          <div
            className="grid grid-cols-6 gap-2 mt-2"
            data-testid="review-tile-grid"
          >
            {QUESTIONS.map((q) => {
              const answered = answers[q.number] != null;
              const isCurrent = q.number === current.number;
              const state = isCurrent ? "current" : answered ? "answered" : "missing";
              const cls = {
                current: "bg-[#C86B5E] text-white border-[#C86B5E] shadow-md scale-105",
                answered: "bg-[#EBF2EA] text-[#2D2A26] border-[#8A9A86]/40 hover:border-[#8A9A86]",
                missing: "bg-white text-[#8A8782] border-dashed border-[#D1C7B8] hover:border-[#C86B5E] hover:text-[#2D2A26]",
              }[state];
              return (
                <button
                  key={q.number}
                  onClick={() => { jumpTo(q.number); setReviewOpen(false); }}
                  data-testid={`review-tile-${q.number}`}
                  data-state={state}
                  aria-current={isCurrent ? "true" : undefined}
                  aria-label={`Question ${q.number} — ${state}`}
                  className={[
                    "aspect-square rounded-xl border-2 font-semibold text-sm flex items-center justify-center transition-all active:scale-95",
                    cls,
                  ].join(" ")}
                >
                  {q.number}
                </button>
              );
            })}
          </div>

          {allAnswered ? (
            <button
              onClick={() => { setReviewOpen(false); submit(); }}
              disabled={submitting}
              data-testid="review-submit-button"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3 font-medium hover:bg-[#B35B4E] disabled:opacity-50 transition-all"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Submit and see results
            </button>
          ) : (
            <button
              onClick={() => { jumpTo(missingNumbers[0]); setReviewOpen(false); }}
              data-testid="review-jump-first-missing-button"
              className="mt-2 inline-flex items-center justify-center gap-2 bg-[#2D2A26] text-white rounded-full px-6 py-3 font-medium text-sm hover:bg-[#1f1d1a] transition-all"
            >
              Jump to first missing
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quiz;
